import {
  ActionPermission,
  POSOperationPermission,
  PosCardPermission,
  SettingsCardPermission,
  SettingsTabPermission,
  SidebarPermission,
  UserPermissions,
} from "./permissionTypes";
import { ACTION_CATALOG, POS_CARD_CATALOG, SETTINGS_CARD_CATALOG } from "./permissionCatalog";

export const PREDEFINED_ROLE_IDS = ["admin", "manager", "supervisor", "cashier", "server", "clock"] as const;

export type RestaurantRoleId = (typeof PREDEFINED_ROLE_IDS)[number];

export type RestaurantRole = {
  id: string;
  name: string;
  system: boolean;
  channel: "email" | "pin" | "both";
  sidebar: SidebarPermission;
  settingsTabs: SettingsTabPermission;
  settingsCards: SettingsCardPermission;
  posCards: PosCardPermission;
  actions: Record<string, ActionPermission>;
};

type ActionSeed = Record<string, ActionPermission>;

const allow = (extra?: Partial<ActionPermission>): ActionPermission => ({ grant: "allow", ...extra });
const approval = (): ActionPermission => ({ grant: "approval" });
const deny = (): ActionPermission => ({ grant: "deny" });

const blankSidebar = (): SidebarPermission => ({
  pos: false,
  kds: false,
  store: false,
  reservations: false,
  customers: false,
  menus: false,
  analytics: false,
  layout: false,
  settings: false,
  mev: false,
});

const blankTabs = (): SettingsTabPermission => ({
  storeDetails: false,
  storeOperation: false,
  menu: false,
  order: false,
  payment: false,
  print: false,
  webhooks: false,
  users: false,
  others: false,
  tables: false,
  website: false,
  cloudSync: false,
});

const cardsFor = (tabs: SettingsTabPermission, force?: boolean): SettingsCardPermission => {
  const cards: SettingsCardPermission = {};
  Object.entries(SETTINGS_CARD_CATALOG).forEach(([tab, list]) => {
    cards[tab] = {};
    list.forEach((card) => {
      cards[tab][card.key] = force === true || tabs[tab as keyof SettingsTabPermission] === true;
    });
  });
  return cards;
};

const cardsForKeys = (allowed: Record<string, string[]>): SettingsCardPermission => {
  const cards: SettingsCardPermission = {};
  Object.entries(SETTINGS_CARD_CATALOG).forEach(([tab, list]) => {
    cards[tab] = {};
    const keys = allowed[tab] || [];
    list.forEach((card) => {
      cards[tab][card.key] = keys.includes(card.key);
    });
  });
  return cards;
};

const posCards = (on: string[] | "all"): PosCardPermission => {
  const cards: PosCardPermission = {};
  POS_CARD_CATALOG.forEach((card) => {
    cards[card.key] = on === "all" || on.includes(card.key);
  });
  return cards;
};

const actionsFrom = (seed: ActionSeed): Record<string, ActionPermission> => {
  const actions: Record<string, ActionPermission> = {};
  ACTION_CATALOG.forEach((action) => {
    actions[action.key] = seed[action.key] || deny();
  });
  return actions;
};

const floorActions = (seed: ActionSeed): ActionSeed => ({
  createOrders: allow(),
  editOpenOrders: allow(),
  acceptPayments: allow(),
  viewKitchenOrders: allow(),
  viewOwnSales: allow(),
  clockInOut: allow(),
  updateKitchenStatus: deny(),
  manageEmployees: deny(),
  changeMenuPrices: deny(),
  manageRestaurantSettings: deny(),
  ...seed,
});

export const RESTAURANT_ROLES: RestaurantRole[] = [
  {
    id: "admin",
    name: "Admin",
    system: true,
    channel: "both",
    sidebar: { ...blankSidebar(), pos: true, kds: true, store: true, reservations: true, customers: true, menus: true, analytics: true, layout: true, settings: true, mev: true },
    settingsTabs: {
      storeDetails: true,
      storeOperation: true,
      menu: true,
      order: true,
      payment: true,
      print: true,
      webhooks: true,
      users: true,
      others: true,
      tables: true,
      website: true,
      cloudSync: true,
    },
    settingsCards: cardsFor(blankTabs(), true),
    posCards: posCards("all"),
    actions: actionsFrom({
      createOrders: allow(),
      editOpenOrders: allow(),
      accessOthersOrders: allow(),
      acceptPayments: allow(),
      cancelUnpaidOrders: allow(),
      refundPayments: allow(),
      applyDiscounts: allow(),
      openCashDrawer: allow(),
      closeShift: allow(),
      endOfDay: allow(),
      viewOwnSales: allow(),
      viewAllSales: allow(),
      manageEmployees: allow(),
      changeMenuPrices: allow(),
      manageRestaurantSettings: allow(),
      viewKitchenOrders: allow(),
      updateKitchenStatus: allow(),
      clockInOut: allow(),
      viewLabourReport: allow(),
      editAttendance: allow(),
      forceClockOut: allow(),
      endOtherShift: allow(),
      openDrawerSession: allow(),
      cashIn: allow(),
      cashOut: allow(),
      cashDrop: allow(),
      countCash: allow(),
      closeDrawerSession: allow(),
      approveCashVariance: allow(),
      handoverDrawer: allow(),
      viewDrawerReports: allow(),
    }),
  },
  {
    id: "manager",
    name: "Manager",
    system: true,
    channel: "both",
    sidebar: { ...blankSidebar(), pos: true, kds: true, store: true, reservations: true, customers: true, menus: true, analytics: true, layout: true, settings: true },
    settingsTabs: {
      storeDetails: true,
      storeOperation: true,
      menu: true,
      order: true,
      payment: true,
      print: true,
      webhooks: false,
      users: false,
      others: true,
      tables: true,
      website: true,
      cloudSync: false,
    },
    settingsCards: cardsFor({
      storeDetails: true,
      storeOperation: true,
      menu: true,
      order: true,
      payment: true,
      print: true,
      webhooks: false,
      users: false,
      others: true,
      tables: true,
      website: true,
      cloudSync: false,
    }),
    posCards: posCards("all"),
    actions: actionsFrom({
      createOrders: allow(),
      editOpenOrders: allow(),
      accessOthersOrders: allow(),
      acceptPayments: allow(),
      cancelUnpaidOrders: allow(),
      refundPayments: allow(),
      applyDiscounts: allow(),
      openCashDrawer: allow(),
      closeShift: allow(),
      endOfDay: allow(),
      viewOwnSales: allow(),
      viewAllSales: allow(),
      manageEmployees: allow(),
      changeMenuPrices: allow(),
      manageRestaurantSettings: allow(),
      viewKitchenOrders: allow(),
      updateKitchenStatus: allow(),
      clockInOut: allow(),
      viewLabourReport: allow(),
      editAttendance: allow(),
      forceClockOut: allow(),
      endOtherShift: allow(),
      openDrawerSession: allow(),
      cashIn: allow(),
      cashOut: allow(),
      cashDrop: allow(),
      countCash: allow(),
      closeDrawerSession: allow(),
      approveCashVariance: allow(),
      handoverDrawer: allow(),
      viewDrawerReports: allow(),
    }),
  },
  {
    id: "supervisor",
    name: "Shift Supervisor",
    system: true,
    channel: "pin",
    sidebar: { ...blankSidebar(), pos: true, kds: true, reservations: true, customers: true, layout: true },
    settingsTabs: blankTabs(),
    settingsCards: cardsFor(blankTabs()),
    posCards: posCards(["categories", "products", "savedCart", "tables", "orders", "takeaway", "delivery", "reservations", "online", "transactions", "customers", "timeClock"]),
    actions: actionsFrom(floorActions({
      accessOthersOrders: allow(),
      cancelUnpaidOrders: allow(),
      refundPayments: approval(),
      applyDiscounts: approval(),
      openCashDrawer: allow(),
      closeShift: allow(),
      endOfDay: allow(),
      viewAllSales: allow(),
      updateKitchenStatus: allow(),
      viewLabourReport: allow(),
      editAttendance: allow(),
      forceClockOut: allow(),
      openDrawerSession: allow(),
      cashIn: allow(),
      cashOut: allow(),
      cashDrop: allow(),
      countCash: allow(),
      closeDrawerSession: allow(),
      approveCashVariance: allow(),
      handoverDrawer: allow(),
      viewDrawerReports: allow(),
    })),
  },
  {
    id: "cashier",
    name: "Cashier",
    system: true,
    channel: "pin",
    sidebar: { ...blankSidebar(), pos: true, reservations: true, customers: true },
    settingsTabs: blankTabs(),
    settingsCards: cardsFor(blankTabs()),
    posCards: posCards(["categories", "products", "savedCart", "orders", "takeaway", "transactions", "customers", "timeClock"]),
    actions: actionsFrom(floorActions({
      cancelUnpaidOrders: approval(),
      refundPayments: approval(),
      applyDiscounts: approval(),
      openCashDrawer: allow({ assignedOnly: true }),
      closeShift: allow({ assignedOnly: true }),
      openDrawerSession: allow({ assignedOnly: true }),
      cashIn: allow({ assignedOnly: true }),
      cashOut: allow({ assignedOnly: true }),
      cashDrop: allow({ assignedOnly: true }),
      countCash: allow({ assignedOnly: true }),
      closeDrawerSession: allow({ assignedOnly: true }),
      handoverDrawer: allow({ assignedOnly: true }),
    })),
  },
  {
    id: "server",
    name: "Server",
    system: true,
    channel: "pin",
    sidebar: { ...blankSidebar(), pos: true, reservations: true, customers: true, layout: true },
    settingsTabs: blankTabs(),
    settingsCards: cardsForKeys({ tables: ["tableManagement"] }),
    posCards: posCards(["categories", "products", "savedCart", "tables", "orders", "takeaway", "reservations", "customers", "timeClock"]),
    actions: actionsFrom(floorActions({
      editOpenOrders: allow({ assignedOnly: true }),
      cancelUnpaidOrders: approval(),
      refundPayments: approval(),
      applyDiscounts: approval(),
      closeShift: allow({ assignedOnly: true }),
    })),
  },
  {
    id: "clock",
    name: "Clock-in / clock-out",
    system: true,
    channel: "pin",
    sidebar: blankSidebar(),
    settingsTabs: blankTabs(),
    settingsCards: cardsFor(blankTabs()),
    posCards: posCards([]),
    actions: actionsFrom({ clockInOut: allow() }),
  },
];

export const canonicalRoleId = (role?: string | null): string => {
  const value = String(role || "").trim();
  if (value === "member") return "cashier";
  if ((PREDEFINED_ROLE_IDS as readonly string[]).includes(value)) return value;
  if (value.startsWith("custom_")) return value;
  return "cashier";
};

export const roleName = (roleId: string): string => {
  return RESTAURANT_ROLES.find((role) => role.id === roleId)?.name || roleId;
};

export const codeRole = (roleId: string): RestaurantRole | null => {
  return RESTAURANT_ROLES.find((role) => role.id === roleId) || null;
};

export const cloneRole = (role: RestaurantRole): RestaurantRole => JSON.parse(JSON.stringify(role));

const deriveOperations = (role: RestaurantRole): POSOperationPermission => {
  const allowed = (key: string) => role.actions[key]?.grant === "allow";
  return {
    createOrders: allowed("createOrders"),
    completeOrders: allowed("acceptPayments"),
    processRefunds: allowed("refundPayments"),
    viewReports: allowed("viewAllSales"),
    manageTables: role.posCards.tables === true,
    manageReservations: role.posCards.reservations === true,
    manageCustomers: role.posCards.customers === true,
    managePOSUsers: allowed("manageEmployees"),
    endOfDay: allowed("endOfDay"),
  };
};

export const permissionsFromRole = (role: RestaurantRole): UserPermissions => ({
  sidebar: role.sidebar,
  settingsTabs: role.settingsTabs,
  settingsCards: role.settingsCards,
  posCards: role.posCards,
  actions: role.actions,
  posOperations: deriveOperations(role),
});

export const mergeRole = (roleId: string, saved?: Partial<RestaurantRole> | null): RestaurantRole => {
  const base = codeRole(roleId) || {
    id: roleId,
    name: saved?.name || "Role",
    system: false,
    channel: "both" as const,
    sidebar: blankSidebar(),
    settingsTabs: blankTabs(),
    settingsCards: cardsFor(blankTabs()),
    posCards: posCards([]),
    actions: actionsFrom({}),
  };
  if (!saved) return cloneRole(base);
  return {
    ...base,
    ...saved,
    id: roleId,
    name: saved.name || base.name,
    system: base.system,
    sidebar: { ...base.sidebar, ...(saved.sidebar || {}) },
    settingsTabs: { ...base.settingsTabs, ...(saved.settingsTabs || {}) },
    settingsCards: { ...base.settingsCards, ...(saved.settingsCards || {}) },
    posCards: { ...base.posCards, ...(saved.posCards || {}) },
    actions: { ...base.actions, ...(saved.actions || {}) },
  };
};

export const emailRoleId = (store: any, email: string): string | null => {
  const target = String(email || "").trim().toLowerCase();
  if (!target || !store) return null;
  const assigned = (Array.isArray(store.roleAssignments) ? store.roleAssignments : []).find(
    (row: any) => String(row?.email || "").trim().toLowerCase() === target
  );
  if (assigned?.roleId) return canonicalRoleId(assigned.roleId);
  if (String(store.createdBy || "").trim().toLowerCase() === target) return "admin";
  if ((store.admins || []).some((item: string) => String(item).trim().toLowerCase() === target)) return "admin";
  if ((store.members || []).some((item: string) => String(item).trim().toLowerCase() === target)) return "manager";
  if ((store.kdsUsers || []).some((item: string) => String(item).trim().toLowerCase() === target)) return "kds";
  return null;
};

export const actionDecision = (
  permissions: UserPermissions | null | undefined,
  action: string,
  amount?: number | null
): "allow" | "approval" | "deny" => {
  const spec = permissions?.actions?.[action];
  if (!spec || spec.grant === "deny") return "deny";
  if (spec.grant === "approval") return "approval";
  if (spec.maxAmount != null && amount != null && Number(amount) > Number(spec.maxAmount)) return "approval";
  return "allow";
};
